import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/config';
import { EQUIPMENT_MODELS_RESOURCE } from '../../api/endpoints';
import { extractList, extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import {
  buildEquipmentModelRelationParams,
  formatEquipmentModelDate,
  getEquipmentModelAuthorLabel,
  getEquipmentModelStatusLabel,
} from './equipmentModelApiHelpers';
import { EQUIPMENT_MODEL_PERMISSIONS } from './equipmentModelPermissions';

const EquipmentModelList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const canCreate = hasPermission(EQUIPMENT_MODEL_PERMISSIONS.CREATE);
  const canView = hasPermission(EQUIPMENT_MODEL_PERMISSIONS.VIEW);
  const canEdit = hasPermission(EQUIPMENT_MODEL_PERMISSIONS.EDIT);
  const canDelete = hasPermission(EQUIPMENT_MODEL_PERMISSIONS.DELETE);
  const canAnyAction = canView || canEdit || canDelete;
  const colSpan = canAnyAction ? 6 : 5;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(EQUIPMENT_MODELS_RESOURCE.LIST, {
        params: {
          page: pagination.page,
          per_page: pagination.per_page,
          paginate: 1,
          ...buildEquipmentModelRelationParams(),
          ...(search ? { search } : {}),
        },
      });
      setRows(extractList(data));
      const pag = extractPagination(data);
      setPagination((prev) => ({ ...prev, total: pag.total ?? 0 }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load equipment models');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/equipment-models/view/${id}`);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/equipment-models/edit/${id}`);
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();

    const result = await SweetAlert.confirm({
      title: 'Delete equipment model?',
      text: 'This action cannot be undone.',
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await api.delete(EQUIPMENT_MODELS_RESOURCE.BY_ID(row.id));
      toast.success('Equipment model deleted');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete equipment model');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Equipment Models
          </Typography>
          <Can permission={EQUIPMENT_MODEL_PERMISSIONS.CREATE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/equipment-models/create')}
            >
              Create equipment model
            </Button>
          </Can>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search equipment models..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 320 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created at</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    {canAnyAction && (
                      <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No equipment models found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover={canView}
                        onClick={() =>
                          canView && row.id && navigate(`/equipment-models/view/${row.id}`)
                        }
                        sx={{ cursor: canView && row.id ? 'pointer' : 'default' }}
                      >
                        <TableCell>{row.name ?? '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Tooltip title={row.description ?? ''}>
                            <Typography variant="body2" noWrap>
                              {row.description ?? '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatEquipmentModelDate(row.created_at)}</TableCell>
                        <TableCell>{getEquipmentModelAuthorLabel(row)}</TableCell>
                        <TableCell>{getEquipmentModelStatusLabel(row)}</TableCell>
                        {canAnyAction && (
                          <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, row)}
                              disabled={actionLoadingId === row.id}
                              aria-label="Actions"
                            >
                              {actionLoadingId === row.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <MoreVertIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Can permission={EQUIPMENT_MODEL_PERMISSIONS.VIEW}>
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission={EQUIPMENT_MODEL_PERMISSIONS.EDIT}>
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission={EQUIPMENT_MODEL_PERMISSIONS.DELETE}>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default EquipmentModelList;
